/**
 * @file footer-categories.ts
 * @description Какие категории соберёт автосписок «Категории каталога» в
 * подвале (phase19.16, приёмка Р2а; uiux-footer-columns §4.4 «авто, N кат.»,
 * §4.6 — список серым).
 *
 * Повторяет правило сервера `catalogMenuCategories` (публичные «листья» с
 * товарами — если у категории есть подкатегории с товарами, идём в них;
 * по убыванию числа товаров) по списку категорий кабинета. Счёт товаров
 * кабинета — видимый сотруднику, поэтому список приблизительный: точный
 * собирает витрина при публикации.
 */

import type { CatalogCategory } from "../api-catalog";

/** Видна ли категория на сайте: активна и не снята с публикации (tristate наследуется от родителя). */
function publishedOnSite(category: CatalogCategory, byId: ReadonlyMap<string, CatalogCategory>): boolean {
  let node: CatalogCategory | undefined = category;
  while (node) {
    if (!node.isActive) return false;
    if (node.isPublishedOnSite !== null) return node.isPublishedOnSite;
    node = node.parentId ? byId.get(node.parentId) : undefined;
  }
  return true;
}

/** Названия категорий автосписка по порядку показа (без ограничения «сколько показывать»). */
export function footerAutoCategories(categories: readonly CatalogCategory[]): string[] {
  const byId = new Map(categories.map((c) => [c.id, c]));
  const own = categories.filter((c) => !c.isSystemRoot && !c.isExternal && publishedOnSite(c, byId));
  const ownIds = new Set(own.map((c) => c.id));
  const count = (c: CatalogCategory): number => c.productsCountTotal ?? c.productsCount ?? 0;
  const kids = new Map<string, CatalogCategory[]>();
  for (const c of own) {
    if (c.parentId && ownIds.has(c.parentId)) kids.set(c.parentId, [...(kids.get(c.parentId) ?? []), c]);
  }
  const leaves: CatalogCategory[] = [];
  const walk = (nodes: readonly CatalogCategory[]): void => {
    for (const node of nodes) {
      const withProducts = (kids.get(node.id) ?? []).filter((k) => count(k) > 0);
      if (withProducts.length > 0) walk(withProducts);
      else if (count(node) > 0) leaves.push(node);
    }
  };
  walk(own.filter((c) => !c.parentId || !ownIds.has(c.parentId)));
  return leaves.sort((a, b) => count(b) - count(a)).map((c) => c.displayName);
}
